/**
 * Purpose: Game setup flow — add players, configure game mode/schedule, and start playing.
 * Owns: Setup screen layout orchestrating PlayerSetup and GameSetup components.
 *       Holds local state for custom subject order (only needed during setup).
 * Notes: This page is an orchestrator only (CS-17) — complex child components
 *        live in their own files. Loads the question pack on mount.
 *        Game mode and schedule mode are stored in GameContext state.
 *        Custom subject order is local state since it's only used at game start.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { PlayerSetup } from '../components/setup/PlayerSetup';
import { GameSetup } from '../components/setup/GameSetup';
import familyRoadTripPack from '../data/packs/base';
import { REGULAR_SUBJECTS } from '../config/classConfig';
import type { Category } from '../types/question';

export function SetupPage() {
  const navigate = useNavigate();
  const {
    state,
    addPlayer,
    removePlayer,
    setGameMode,
    setScheduleMode,
    startGame,
    loadPack,
  } = useGame();

  /**
   * Custom subject order — local to setup because it's only consumed
   * at game start time (passed to startGame). Initialized with the
   * default subject order from classConfig (CS-21).
   */
  const [customSubjectOrder, setCustomSubjectOrder] = useState<Category[]>(
    () => [...REGULAR_SUBJECTS],
  );

  /* Load the default question pack on mount */
  useEffect(() => {
    loadPack(familyRoadTripPack);
  }, [loadPack]);

  function handleStartGame() {
    /* Pass custom subject order only when schedule mode is 'choose' */
    const subjectOrder =
      state.scheduleMode === 'choose' ? customSubjectOrder : undefined;
    startGame(subjectOrder);
    navigate('/play');
  }

  return (
    <div className="chalkboard-bg" style={styles.page}>
      <h1 className="chalk-text-bright" style={styles.heading}>Game Setup</h1>

      <PlayerSetup
        players={state.players}
        onAddPlayer={(player) => addPlayer(player.name, player.ageGroup, player.tier)}
        onRemovePlayer={removePlayer}
      />

      <GameSetup
        playerCount={state.players.length}
        selectedPackId={state.config.packId}
        gameMode={state.gameMode}
        onGameModeChange={setGameMode}
        scheduleMode={state.scheduleMode}
        onScheduleModeChange={setScheduleMode}
        customSubjectOrder={customSubjectOrder}
        onSubjectOrderChange={setCustomSubjectOrder}
        onStartGame={handleStartGame}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    overflowY: 'auto',
  },
  heading: {
    fontSize: '1.8rem',
    fontWeight: '400',
    textAlign: 'center' as const,
    paddingTop: '0.5rem',
  },
};
